"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { fetchEbooks } from "@/utils/api";
import EbookCard from "@/components/EbookCard";
import { EBOOK_CATEGORIES } from "@/constants/ebookCategories";
import HorizontalShelf from "@/components/ui/HorizontalShelf";

function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}
function Shelf({ title, items }) {
  if (!items?.length) return null;

  return (
    <div className="mb-8">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold opacity-90">{title}</h2>
        <span className="text-xs text-textSecondary">Swipe →</span>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((book) => {
          const key = book?._id ?? book?.id;
          return (
            <div key={key} className="w-[170px] shrink-0">
              <EbookCard book={book} variant="grid" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function EbooksPage() {

  const [books, setBooks] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
const qDebounced = useDebouncedValue(q, 300);
const showGrid = qDebounced.trim().length > 0 || Boolean(category);

  const shelves = useMemo(() => {
  const all = [...books];

 const byNewest = (a, b) =>
  new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime();


  const byBestSellers = (a, b) =>
    (b.purchasesCount || 0) - (a.purchasesCount || 0);

  const byTrending = (a, b) =>
    (b.views || 0) - (a.views || 0);

  const now = Date.now();

  const featured = all
    .filter(
      (b) =>
        b.featured === true &&
        (!b.featuredUntil || new Date(b.featuredUntil).getTime() > now)
    )
    .sort((a, b) => (a.featuredRank || 0) - (b.featuredRank || 0))
    .slice(0, 10);
const featuredOrFallback =
  featured.length > 0 ? featured : [...all].sort(byNewest).slice(0, 10);


 return {
  all,
  featured: featuredOrFallback,
  newReleases: [...all].sort(byNewest).slice(0, 10),
  bestSellers: [...all].sort(byBestSellers).slice(0, 10),
  trending: [...all].sort(byTrending).slice(0, 10),
  free: all.filter((b) => Number(b.pricePi) === 0).slice(0, 10),
};

}, [books]);

useEffect(() => {
  let cancelled = false;

  (async () => {
    setLoading(true);
    setErr("");
    try {
      const { list } = await fetchEbooks({
        q: qDebounced,
        category: category || undefined,
      });
      
//console.log("LIST[0] keys:", Object.keys(list?.[0] || {}));
//console.log("LIST[0].slug:", list?.[0]?.slug);

      if (!cancelled) setBooks(list || []);
    } catch (e) {
      if (!cancelled) setErr(e?.message || "Failed to load ebooks");
    } finally {
      if (!cancelled) setLoading(false);
    }
  })();

  return () => {
    cancelled = true;
  };
}, [qDebounced, category]);



return (
  <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8 text-textPrimary">
    {/* Header row */}
  {/* Header row */}
<div className="flex items-start justify-between gap-3 mb-4">
  <div>
    <h1 className="text-2xl font-semibold">Ebooks Marketplace</h1>

    <p className="text-sm text-textSecondary mt-1">
      Browse, buy, and read ebooks inside ReloadPi.
    </p>

    <Link
      href="/creator/ebooks/promo"
      className="mt-2 inline-flex text-sm text-primary underline underline-offset-4"
    >
      Creator guidelines →
    </Link>
  </div>

  <Link
    href="/creator/ebooks/new"
    className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-black hover:opacity-90 shadow-glowPrimary"
  >
    Upload ebook
  </Link>
</div>


    {/* Toolbar */}
    <div className="mb-6 rounded-2xl border border-border bg-surface/60 p-3">
      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Search by title, description, or author"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[220px] rounded-xl border border-border bg-background/40 px-3 py-2 text-sm outline-none focus:border-borderStrong"
        />

        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="rounded-xl border border-border bg-background/40 px-3 py-2 text-sm outline-none focus:border-borderStrong"
        >
          <option value="" className="bg-background">All categories</option>
          {EBOOK_CATEGORIES.map((c) => (
            <option key={c.key} value={c.key} className="bg-background">
              {c.label}
            </option>
          ))}
        </select>
      </div>
    </div>

    {loading && <p className="text-sm mb-3 text-textSecondary">Loading ebooks…</p>}

    {err && (
      <div className="mb-4 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs">
        {err}
      </div>
    )}

    {/* All books grid */}
   {!loading && !err && showGrid && (
  <>
    <div className="flex items-end justify-between mb-3">
      <h2 className="text-sm font-semibold opacity-90">Results</h2>
      <span className="text-xs text-textSecondary">
        {shelves.all.length} item{shelves.all.length === 1 ? "" : "s"}
      </span>
    </div>

    <div className="grid gap-5 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 items-start">
      {shelves.all.map((book) => {
        const key = book?._id ?? book?.id;
        return <EbookCard key={key} book={book} variant="grid" />;
      })}
    </div>
  </>
)}

{!loading && !err && shelves.all.length > 0 && !showGrid && (
  <>
    <HorizontalShelf
      title="Best sellers"
      items={shelves.bestSellers}
      renderItem={(book) => (
        <div key={book?._id ?? book?.id} className="w-[170px] shrink-0" data-card>
          <EbookCard book={book} variant="grid" />
        </div>
      )}
    />

    <HorizontalShelf
      title="New releases"
      items={shelves.newReleases}
      renderItem={(book) => (
        <div key={book?._id ?? book?.id} className="w-[170px] shrink-0" data-card>
          <EbookCard book={book} variant="grid" />
        </div>
      )}
    />

    <HorizontalShelf
      title="Featured"
      items={shelves.featured}
      renderItem={(book) => (
        <div key={book?._id ?? book?.id} className="w-[170px] shrink-0" data-card>
          <EbookCard book={book} variant="grid" />
        </div>
      )}
    />

    <HorizontalShelf
      title="Trending now"
      items={shelves.trending}
      renderItem={(book) => (
        <div key={book?._id ?? book?.id} className="w-[170px] shrink-0" data-card>
          <EbookCard book={book} variant="grid" />
        </div>
      )}
    />

    <HorizontalShelf
      title="Free ebooks"
      items={shelves.free}
      renderItem={(book) => (
        <div key={book?._id ?? book?.id} className="w-[170px] shrink-0" data-card>
          <EbookCard book={book} variant="grid" />
        </div>
      )}
    />
  </>
)}
  </div>
);
}
